import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Loader2, Save, Check, Info } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { USE_SUPABASE } from "@/lib/dataSource";
import { useBiens } from "@/hooks/useBiens";
import { invalidateFieldPermissionsCache } from "@/hooks/useFieldPermissions";
import { SelectField } from "@/components/ui/SelectField";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { BienDetailPage } from "@/pages/biens/BienDetailPage";
import { BienVenteDetailPage } from "@/v2/pages/BienVenteDetailPage";
import {
  DROITS_ROLES_HUMAINS,
  ROLE_EFFECTIF_FICHE,
  FieldPermsEditorProvider,
  fromPermState,
  toPermState,
  isPresetRole,
  PRESET_OF_ROLE,
  type DroitsRoleHumain,
  type FieldPermsEditor,
  type PermState,
} from "@/contexts/fieldPermsEditor";
import { invalidateBienFichePresetCache } from "@/hooks/useBienFichePreset";

/**
 * Réglage des droits par champ — « Droits et rôles ».
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * CE QUI CHANGE PAR RAPPORT À L'ANCIENNE PAGE
 * ─────────────────────────────────────────────────────────────────────────────
 * L'ancienne version empilait 252 clés en une table à double colonne par rôle.
 * Deux défauts, constatés en prod le 2026-08-10 : plus de la moitié des clés ne
 * concernent pas la fiche location (dont 350 lignes de fantômes `els_*`), et il
 * fallait connaître le nom technique d'un champ pour le retrouver.
 *
 * Ici, on monte LA FICHE elle-même dans un contexte de réglage. Le helper
 * `F()` de `BienDetailPage` — par lequel passent 105 des 113 champs — rend un
 * sélecteur à trois états au lieu du champ. Onglets, blocs, grille et ordre
 * sont donc ceux de la fiche, par construction : la page ne peut pas dériver.
 *
 * ⚠️ La fiche montée ici n'écrit RIEN : `saveBien`, `handleSave`, `handleDelete`
 * et le bouton « Lancer la mission » sont neutralisés quand ce contexte est
 * actif (cf. `permsMode` dans BienDetailPage). Sans ça, son autosave débouncé
 * à 1,5 s modifierait un bien réel pendant qu'on règle des permissions.
 *
 * Le même écran sert les DEUX fiches — location et vente. Elles partagent la
 * table `field_permissions` mais pas leurs champs : d'où deux entrées de menu
 * distinctes plutôt qu'une bascule enfouie dans la page.
 */
export function DroitsChampsPage({ fiche = "location" }: { fiche?: "location" | "vente" } = {}) {
  const { t } = useTranslation();

  const [roleHumain, setRoleHumain] = useState<DroitsRoleHumain>("collaborateur");
  const roleEffectif = ROLE_EFFECTIF_FICHE[roleHumain];
  const presetKey = isPresetRole(roleHumain) ? PRESET_OF_ROLE[roleHumain] : null;

  /** État courant, et sa copie de référence pour calculer le diff. */
  const [etats, setEtats] = useState<Record<string, PermState>>({});
  const [reference, setReference] = useState<Record<string, PermState>>({});
  /** Rôle dont les droits sont effectivement en mémoire. Sert à dériver `loading`. */
  const [loadedRole, setLoadedRole] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  /**
   * Champs actuellement À L'ÉCRAN, alimentés par le rendu de la fiche.
   * La fiche ne rendant que l'onglet actif, cet ensemble EST l'onglet courant —
   * c'est ce qui donne les actions groupées sans modéliser les onglets ici.
   */
  const [visibles, setVisibles] = useState<Record<string, string>>({});

  // ── Chargement des droits du rôle EFFECTIF ────────────────────────────────
  // `loading` est DÉRIVÉ plutôt que posé par un setState dans le corps de
  // l'effet : appeler setState là déclenche un rendu en cascade (react-hooks).
  // Comparer le rôle chargé au rôle demandé dit la même chose, gratuitement,
  // et gère le changement de rôle sans état intermédiaire.
  // Clé sur le rôle HUMAIN, pas l'effectif : "collaborateur",
  // "collaborateur_apparts" et "collaborateur_pipeline" partagent le même
  // rôle effectif collaborateur_afr mais chargent depuis des sources
  // différentes (field_permissions vs bien_fiche_presets) — une clé sur
  // roleEffectif seul masquerait ce changement de source en gardant
  // silencieusement les données du précédent affichées.
  const loading = loadedRole !== roleHumain;
  useEffect(() => {
    let cancelled = false;
    const query = presetKey
      ? supabase.from("bien_fiche_presets").select("item_key, visible").eq("preset", presetKey)
      : supabase.from("field_permissions").select("field_key, can_see, can_edit").eq("role", roleEffectif);
    query.then(({ data, error }) => {
      if (cancelled) return;
      if (error) {
        console.error("[DroitsChampsPage]", error.message);
        // Sans ça, un chargement en échec (ex. table bien_fiche_presets pas
        // encore appliquée) laisserait affichées les données du PRÉCÉDENT
        // rôle/préréglage chargé avec succès, mais étiquetées comme celles du
        // nouveau — état honnête vide plutôt que données périmées mal nommées.
        setEtats({});
        setReference({});
        setLoadedRole(roleHumain);
        return;
      }
      const map: Record<string, PermState> = {};
      if (presetKey) {
        for (const r of (data ?? []) as { item_key: string; visible: boolean }[]) {
          map[r.item_key] = r.visible ? "view" : "hidden";
        }
      } else {
        for (const r of (data ?? []) as { field_key: string; can_see: boolean; can_edit: boolean }[]) {
          map[r.field_key] = toPermState(r.can_see, r.can_edit);
        }
      }
      setEtats(map);
      setReference(map);
      setLoadedRole(roleHumain);
    });
    return () => { cancelled = true; };
  }, [roleHumain, roleEffectif, presetKey]);

  // ── Contrat passé à la fiche ──────────────────────────────────────────────
  // `register`/`unregister` sont STABLES : ils sont dans les dépendances d'un
  // effet côté cellule, une identité changeante les ferait tourner en boucle.
  const register = useCallback((fieldKey: string, label: string) => {
    setVisibles((prev) => (prev[fieldKey] === label ? prev : { ...prev, [fieldKey]: label }));
  }, []);
  const unregister = useCallback((fieldKey: string) => {
    setVisibles((prev) => {
      if (!(fieldKey in prev)) return prev;
      const next = { ...prev };
      delete next[fieldKey];
      return next;
    });
  }, []);

  const editor: FieldPermsEditor = useMemo(() => ({
    role: roleEffectif,
    // Défaut « caché » pour un champ NORMAL d'un rôle réel (absence de ligne
    // = invisible côté RPC, cf. commentaire historique). Défaut « visible »
    // pour un préréglage (spec §6.2 — absence de ligne = visible) ET pour
    // toute clé structurelle (__tab_*/__section_*) même sous un rôle réel :
    // le runtime (useFieldPermissions.canSee, cf. useFieldPermissions.ts)
    // traite déjà l'absence de ligne comme "visible" pour ces clés — sans ce
    // cas particulier ici, l'éditeur affichait "Caché" pour tout onglet/bloc
    // jamais configuré alors qu'il est bel et bien montré aux utilisateurs.
    get: (k) => etats[k] ?? (presetKey || k.startsWith("__") ? "view" : "hidden"),
    set: (k, s) => setEtats((prev) => ({ ...prev, [k]: s })),
    register,
    unregister,
    allowEdit: !presetKey,
  }), [roleEffectif, etats, register, unregister, presetKey]);

  // ── Diff ──────────────────────────────────────────────────────────────────
  const modifies = useMemo(
    () => Object.keys(etats).filter((k) => etats[k] !== reference[k]),
    [etats, reference],
  );

  const appliquerATous = (state: PermState) => {
    const cles = Object.keys(visibles);
    if (cles.length === 0) return;
    setEtats((prev) => {
      const next = { ...prev };
      for (const k of cles) next[k] = state;
      return next;
    });
  };

  /**
   * Récapitulatif de ce qui partira en base, par état d'arrivée.
   *
   * Les actions groupées mettent 50 champs d'un coup : sans ce décompte,
   * « Enregistrer » serait un saut dans le vide. Et ce n'est pas anodin —
   * `supabase` est instancié depuis les variables d'environnement quel que
   * soit `VITE_USE_SUPABASE`, donc cette page écrit la PROD même lancée
   * depuis le serveur de dev.
   */
  const recap = useMemo(() => {
    const r: Record<PermState, number> = { edit: 0, view: 0, hidden: 0 };
    for (const k of modifies) r[etats[k]]++;
    return r;
  }, [modifies, etats]);

  /**
   * ⛔ VERROU MODE MOCK — le client `supabase` de cette page est instancié
   * depuis les variables d'environnement quel que soit VITE_USE_SUPABASE :
   * lancée depuis un serveur de dev en mock, elle écrirait quand même la
   * PRODUCTION (c'est le mécanisme le plus plausible de la perte des
   * migrations 108/109/110, cf. audit 2026-08-17). En mock : lecture
   * autorisée (utile pour constater l'état prod), écriture interdite.
   */
  const mockVerrou = !USE_SUPABASE;

  const enregistrer = async () => {
    if (mockVerrou || modifies.length === 0) return;
    setConfirmOpen(false);
    setSaving(true);
    const { error } = presetKey
      ? await supabase.from("bien_fiche_presets").upsert(
          modifies.map((item_key) => ({
            preset: presetKey,
            item_key,
            visible: etats[item_key] !== "hidden",
            updated_at: new Date().toISOString(),
          })),
          { onConflict: "preset,item_key" },
        )
      : await supabase.from("field_permissions").upsert(
          modifies.map((field_key) => ({
            field_key,
            role: roleEffectif,
            ...fromPermState(etats[field_key]),
          })),
          { onConflict: "field_key,role" },
        );
    setSaving(false);
    if (error) {
      console.error("[DroitsChampsPage] save", error.message);
      return;
    }
    // Sans ça, les écrans déjà ouverts garderaient l'ancienne matrice en
    // mémoire jusqu'au prochain rechargement complet.
    if (presetKey) invalidateBienFichePresetCache(); else invalidateFieldPermissionsCache();
    setReference(etats);
    setJustSaved(true);
    setTimeout(() => setJustSaved(false), 2500);
  };

  // ── Bien support ──────────────────────────────────────────────────────────
  // On monte une fiche réelle : c'est elle qui porte la structure. Le premier
  // bien en location suffit — les branches conditionnelles sont de toute façon
  // forcées ouvertes en mode droits.
  const { data: biens, isLoading: biensLoading } = useBiens("admin");
  const bienSupport = useMemo(
    () => biens?.find((b) => b.transaction === fiche) ?? undefined,
    [biens, fiche],
  );

  // Les préréglages (Apparts for Rent / Pipeline ELS) ne concernent QUE la
  // popup fiche location — la spec (§4, non-objectifs) exclut explicitement
  // la fiche vente. Sans ce filtre, les deux entrées apparaissaient aussi
  // dans le déroulant de /v2/admin/droits-vente.
  const roleOptions = DROITS_ROLES_HUMAINS
    .filter((r) => fiche === "location" || !isPresetRole(r))
    .map((r) => ({
      value: r,
      label: t(`droits.roles.${r}`),
    }));

  return (
    <div className="flex flex-col gap-4">
      {/* ── Bandeau de commande ── */}
      <div className="sticky top-0 z-20 bg-bg border-b border-border pb-3 pt-1 flex flex-col gap-3">
        <div className="flex flex-wrap items-end gap-4">
          <div className="w-56">
            <SelectField
              label={t("droits.role")}
              value={roleHumain}
              onChange={(v) => setRoleHumain(v as DroitsRoleHumain)}
              options={roleOptions}
            />
          </div>

          <div className="flex items-center gap-2 pb-2">
            <span className="text-xs uppercase tracking-wider text-text-muted">{t("droits.appliquerOnglet")}</span>
            {/* « Éditable » n'a pas de sens en mode préréglage : bien_fiche_presets
                n'a qu'une colonne `visible`, aucun concept d'édition (spec §6.2). */}
            {!presetKey && (
              <Button variant="secondary" size="sm" onClick={() => appliquerATous("edit")}>{t("droits.etat.editable")}</Button>
            )}
            <Button variant="secondary" size="sm" onClick={() => appliquerATous("view")}>{t("droits.etat.visible")}</Button>
            <Button variant="secondary" size="sm" onClick={() => appliquerATous("hidden")}>{t("droits.etat.cache")}</Button>
          </div>

          <div className="ml-auto flex items-center gap-3 pb-2">
            {justSaved && (
              <span className="flex items-center gap-1.5 text-xs text-success">
                <Check size={14} /> {t("common.saved")}
              </span>
            )}
            {modifies.length > 0 && (
              <span className="text-xs text-text-muted">
                {t("droits.modifications", { count: modifies.length })}
              </span>
            )}
            <Button variant="primary" size="sm" onClick={() => setConfirmOpen(true)} disabled={mockVerrou || modifies.length === 0 || saving}>
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              <span className="ml-1.5">{t("common.save")}</span>
            </Button>
          </div>
        </div>

        {/* Le déroulant montre le rôle humain, la table stocke le rôle effectif.
            On le dit, sinon on reproduit exactement le piège de la 108. */}
        <p className="flex items-start gap-1.5 text-xs text-text-muted">
          <Info size={13} className="shrink-0 mt-0.5" />
          <span>
            {presetKey ? (
              t("droits.presetNote", { role: roleEffectif })
            ) : (
              <>
                {t("droits.roleEffectifNote", { role: roleEffectif })}
                {roleEffectif !== roleHumain && ` — ${t("droits.roleRemapNote")}`}
              </>
            )}
          </span>
        </p>

        {mockVerrou && (
          <p className="flex items-start gap-1.5 text-xs text-warning">
            <AlertTriangle size={13} className="shrink-0 mt-0.5" />
            <span>{t("droits.mockVerrou")}</span>
          </p>
        )}
      </div>

      {/* ── La fiche, en mode réglage ── */}
      {loading || biensLoading ? (
        <div className="flex items-center gap-2 text-sm text-text-muted py-10">
          <Loader2 size={16} className="animate-spin" /> {t("common.loading")}
        </div>
      ) : !bienSupport ? (
        <p className="text-sm text-text-muted py-10">{t("droits.aucunBien")}</p>
      ) : (
        <FieldPermsEditorProvider value={editor}>
          {fiche === "vente"
            ? <BienVenteDetailPage bienIdOverride={bienSupport.id} />
            : <BienDetailPage bienIdOverride={bienSupport.id} hidePermissionsTab />}
        </FieldPermsEditorProvider>
      )}

      {/* Confirmation avant écriture. Les actions groupées changent 50 champs
          d'un clic, et la table visée est celle de PRODUCTION même depuis le
          serveur de dev : on récapitule avant de partir. */}
      <Modal isOpen={confirmOpen} onClose={() => setConfirmOpen(false)} title={t("droits.confirmTitle")}>
        <p className="text-sm text-text-primary mb-3">
          {/* En mode préréglage, on n'écrit jamais dans field_permissions ni
              sur un rôle réel — le texte ne doit donc jamais nommer un rôle
              (même branchement que la note sous le déroulant, cf. presetNote). */}
          {presetKey
            ? t("droits.confirmBodyPreset", { count: modifies.length })
            : t("droits.confirmBody", { count: modifies.length, role: roleEffectif })}
        </p>
        <ul className="flex flex-col gap-1 mb-4 pl-4">
          {/* recap.edit reste à 0 en mode préréglage (le bouton "Éditable" et
              l'option "edit" de PermStateCell sont tous deux masqués), mais on
              garde la garde explicite : bien_fiche_presets n'a pas de concept
              d'édition, cette ligne ne doit jamais pouvoir s'afficher ici. */}
          {!presetKey && recap.edit > 0 && <li className="text-sm text-text-secondary list-disc">{t("droits.recapEdit", { count: recap.edit })}</li>}
          {recap.view > 0 && <li className="text-sm text-text-secondary list-disc">{t("droits.recapView", { count: recap.view })}</li>}
          {recap.hidden > 0 && <li className="text-sm text-text-secondary list-disc">{t("droits.recapHidden", { count: recap.hidden })}</li>}
        </ul>
        <div className="flex items-center justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={() => setConfirmOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="primary" size="sm" onClick={enregistrer}>{t("common.save")}</Button>
        </div>
      </Modal>
    </div>
  );
}
